import type { MedplumClient } from '@medplum/core';
import type { Communication, Task } from '@medplum/fhirtypes';
import type { AppointmentRequestDetailsPayload } from '../../messaging/appointmentRequestMessage';
import { APPOINTMENT_REQUEST_DETAILS_EXT_URL } from '../../messaging/appointmentRequestMessage';
import { notifyClientRescheduleDecision } from '../../../lib/notificationLifecycle';
export const APPOINTMENT_RESCHEDULE_TASK = {
  system: 'http://unify.health/tasks',
  code: 'appointment-reschedule-request',
  display: 'Appointment reschedule request',
} as const;

export const APPOINTMENT_RESCHEDULE_TASK_INPUT_TYPE = 'reschedule-details';

/** Open Task statuses that block duplicate reschedule requests. */
export const PENDING_RESCHEDULE_TASK_STATUSES: Task['status'][] = [
  'draft',
  'requested',
  'received',
  'accepted',
  'ready',
  'in-progress',
  'on-hold',
];

export interface CreateRescheduleRequestTaskParams {
  patientId: string;
  appointmentId: string;
  providerId: string;
  episodeOfCareId?: string;
  detailsPayload: AppointmentRequestDetailsPayload;
  communicationId?: string;
  /** Client display name for provider task lists. */
  patientDisplay?: string;
}

import { formatRescheduleRequestTaskDescriptionForProvider } from '../../../lib/portalAppointmentTaskLabels';

/** Short Task.description for provider task lists: label + client name only. */
export function formatRescheduleRequestTaskDescription(patientDisplay?: string): string {
  return formatRescheduleRequestTaskDescriptionForProvider(patientDisplay);
}

export async function createRescheduleRequestTask(
  medplum: MedplumClient,
  params: CreateRescheduleRequestTaskParams,
): Promise<Task | undefined> {
  // The Task is created server-side and may not exist yet; submission must not fail on that race.
  try {
    return await findPendingRescheduleTaskForAppointment(medplum, params.appointmentId);
  } catch (err) {
    console.warn('[createRescheduleRequestTask] Could not look up the reschedule Task:', err);
    return undefined;
  }
}

export async function findPendingRescheduleTaskForAppointment(
  medplum: MedplumClient,
  appointmentId: string,
): Promise<Task | undefined> {
  const tasks = await medplum.searchResources('Task', {
    focus: `Appointment/${appointmentId}`,
    code: `${APPOINTMENT_RESCHEDULE_TASK.system}|${APPOINTMENT_RESCHEDULE_TASK.code}`,
    status: PENDING_RESCHEDULE_TASK_STATUSES.join(','),
    _count: '1',
    _sort: '-_lastUpdated',
  });
  return tasks[0];
}

export async function fetchPendingRescheduleAppointmentIds(
  medplum: MedplumClient,
  patientId: string,
): Promise<Set<string>> {
  const tasks = await medplum.searchResources('Task', {
    patient: `Patient/${patientId}`,
    code: `${APPOINTMENT_RESCHEDULE_TASK.system}|${APPOINTMENT_RESCHEDULE_TASK.code}`,
    status: PENDING_RESCHEDULE_TASK_STATUSES.join(','),
    _count: '100',
  });

  const ids = new Set<string>();
  for (const task of tasks) {
    const ref = task.focus?.reference;
    if (ref?.startsWith('Appointment/')) {
      ids.add(ref.slice('Appointment/'.length));
    }

    const detailsRaw = task.input?.find(
      (item) => item.type?.text === APPOINTMENT_RESCHEDULE_TASK_INPUT_TYPE,
    )?.valueString;
    if (!detailsRaw) continue;
    try {
      const parsed = JSON.parse(detailsRaw) as {
        appointmentId?: string;
        proposedAppointmentId?: string;
      };
      if (parsed.appointmentId) ids.add(parsed.appointmentId);
      if (parsed.proposedAppointmentId) ids.add(parsed.proposedAppointmentId);
    } catch {
      // ignore malformed task input
    }
  }
  return ids;
}

export async function finalizeRescheduleRequest(
  medplum: MedplumClient,
  params: {
    communicationId?: string;
    decision: 'accept' | 'deny';
    originalAppointmentId?: string;
  },
): Promise<void> {
  let requestMessage: Communication | undefined;
  let updatedDetails: AppointmentRequestDetailsPayload | undefined;

  if (params.communicationId) {
    requestMessage = await medplum.readResource('Communication', params.communicationId);
    const extensions = [...(requestMessage.extension ?? [])];
    const detailsIndex = extensions.findIndex((e) => e.url === APPOINTMENT_REQUEST_DETAILS_EXT_URL);
    if (detailsIndex >= 0) {
      const raw = extensions[detailsIndex]?.valueString;
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as AppointmentRequestDetailsPayload;
          parsed.providerDecision = params.decision;
          parsed.respondedAt = new Date().toISOString();
          updatedDetails = parsed;
          extensions[detailsIndex] = {
            ...extensions[detailsIndex]!,
            valueString: JSON.stringify(parsed),
          };
        } catch {
          // leave extension unchanged
        }
      }
    }

    await medplum.updateResource({
      ...requestMessage,
      extension: extensions,
    });
  }

  if (params.originalAppointmentId) {
    await closePendingRescheduleTask(medplum, params.originalAppointmentId, params.decision);
  }

  const patientId = updatedDetails?.patientId?.trim();
  if (!patientId) return;

  const threadRef = requestMessage?.partOf?.[0]?.reference;
  const threadId = threadRef?.startsWith('Communication/')
    ? threadRef.slice('Communication/'.length)
    : undefined;

  try {
    await notifyClientRescheduleDecision(medplum, {
      patientId,
      decision: params.decision,
      preferredWhen: updatedDetails?.preferredWhen,
      threadId,
      requestCommunicationId: params.communicationId,
      appointmentId: params.originalAppointmentId ?? updatedDetails?.appointmentId,
    });
  } catch (err) {
    console.warn('[finalizeRescheduleRequest] Could not create bell notification:', err);
  }
}

export async function closePendingRescheduleTask(
  _medplum: MedplumClient,
  _originalAppointmentId: string,
  _decision: 'accept' | 'deny',
): Promise<void> {
  // No bot call: portal-reschedule respond-reschedule-request closes pending reschedule and appointment-request Tasks.
}
